import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { GoogleWalletBackLink } from '../../../api'
import Button from '../../UI/Button/Button'
import { AdFormSchema } from '../AdForm/AdFormTypes'

const GoogleWalletBackLinks = () => {
  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<AdFormSchema>()

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'passDetails.googleWalletBackLinks',
  })

  const addLink = () => {
    const newLink: GoogleWalletBackLink = {
      id: `link_${Date.now()}`,
      description: '',
      uriValue: '',
      sortOrder: fields.length,
    }
    append(newLink)
  }

  const moveUp = (index: number) => {
    if (index > 0) {
      const currentLinks = watch('passDetails.googleWalletBackLinks') || []
      const newLinks = [...currentLinks]

      // Swap with previous
      ;[newLinks[index - 1], newLinks[index]] = [newLinks[index], newLinks[index - 1]]

      // Update sortOrder values
      newLinks.forEach((link, idx) => {
        link.sortOrder = idx
      })

      setValue('passDetails.googleWalletBackLinks', newLinks)
    }
  }

  const moveDown = (index: number) => {
    if (index < fields.length - 1) {
      const currentLinks = watch('passDetails.googleWalletBackLinks') || []
      const newLinks = [...currentLinks]

      // Swap with next
      ;[newLinks[index], newLinks[index + 1]] = [newLinks[index + 1], newLinks[index]]

      // Update sortOrder values
      newLinks.forEach((link, idx) => {
        link.sortOrder = idx
      })

      setValue('passDetails.googleWalletBackLinks', newLinks)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Google Wallet Back Links</h3>
        <Button type="outline" onClick={addLink} className="text-sm">
          + Add Link
        </Button>
      </div>

      {fields.length === 0 ? (
        <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
          <p>No back links added yet.</p>
          <p className="text-sm">Click &quot;Add Link&quot; to create your first back link.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {fields.map((link, index) => (
            <div key={link.id} className="border rounded-lg p-4 bg-white shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Link {link.sortOrder + 1}</span>
                  <span className="text-xs bg-gray-100 text-gray-500 px-1 py-0.5 rounded" title="Sort Order">
                    #{link.sortOrder}
                  </span>
                </div>

                {/* Reorder buttons */}
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => moveUp(index)}
                    disabled={index === 0}
                    className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                    title="Move up"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => moveDown(index)}
                    disabled={index === fields.length - 1}
                    className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                    title="Move down"
                  >
                    ↓
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="text-red-500 hover:text-red-700 text-sm font-medium"
                >
                  Remove
                </button>
              </div>

              {/* Description Input */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-600 mb-2">Description</label>
                <Controller
                  control={control}
                  name={`passDetails.googleWalletBackLinks.${index}.description`}
                  rules={{ required: 'Description is required' }}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      placeholder="Enter link description (e.g., 'Visit Website', 'Terms & Conditions')"
                      className={`w-full p-3 bg-white border ${
                        errors.passDetails?.googleWalletBackLinks?.[index]?.description
                          ? 'border-red-500'
                          : 'border-gray-300'
                      } rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition duration-200`}
                    />
                  )}
                />
                {errors.passDetails?.googleWalletBackLinks?.[index]?.description && (
                  <p className="text-xs text-red-500 mt-1">
                    {errors.passDetails?.googleWalletBackLinks?.[index]?.description?.message}
                  </p>
                )}
              </div>

              {/* URI Value Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-2">URL</label>
                <Controller
                  control={control}
                  name={`passDetails.googleWalletBackLinks.${index}.uriValue`}
                  rules={{
                    required: 'URL is required',
                    // pattern: {
                    //     value: /^https?:\/\/.+/,
                    //     message: 'Please enter a valid URL starting with http:// or https://'
                    // }
                  }}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="url"
                      placeholder="Enter URL (e.g., https://example.com)"
                      className={`w-full p-3 bg-white border ${
                        errors.passDetails?.googleWalletBackLinks?.[index]?.uriValue
                          ? 'border-red-500'
                          : 'border-gray-300'
                      } rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition duration-200`}
                    />
                  )}
                />
                {errors.passDetails?.googleWalletBackLinks?.[index]?.uriValue && (
                  <p className="text-xs text-red-500 mt-1">
                    {errors.passDetails?.googleWalletBackLinks?.[index]?.uriValue?.message}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default GoogleWalletBackLinks
